import { useState, useEffect } from "react";
import { toast } from "sonner";
import { api } from "@/modules/shared/lib/axios";
import { Entity } from "@/modules/shared/types/entity.type";
import { ResponseAPI } from "@/modules/shared/types/response-api.type";
import { useNavigate } from "react-router-dom";
import { usePromotionSearch } from "./use-promotion-search.hook";

type PromotorCliente = {
	cliente: Entity;
	fkCliente: number;
	fkUsuarioMobile: number;
	fkUsuarioxDispositivo: number;
	fkVisita: number;
	dataInclusaoServidor?: string;
	flagOrigem?: string;
	idProcessamento?: string;
};

export type PromotionVisit = {
	promotor?: Entity;
	promotorCliente: PromotorCliente[];
	fkUsuarioMobile?: number;
	fkUsuarioxDispositivo?: number;
	idVisita?: number;
	fkPromotor: number;
	valor: number;
	observacao: string;
	data: string;
	dataSinc?: string;
	dataInclusao?: string;
	dataInclusaoServidor?: string;
	flagOrigem?: string;
	idProcessamento?: string;
};

// Tipo para as funções de limpeza que podem ser passadas como parâmetro
type ClearStateFunction = () => void;

interface UsePromotionVisitProps {
	clearFilters?: ClearStateFunction;
	clearSelectedVisits?: ClearStateFunction;
	clearAllState?: ClearStateFunction;
}

export function usePromotionVisit(props?: UsePromotionVisitProps) {
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const navigate = useNavigate();

		const { handleSelectPromotionToStartActions } = usePromotionSearch()
	useEffect(() => {
		return () => {
			if (props?.clearFilters) props.clearFilters();
			if (props?.clearSelectedVisits) props.clearSelectedVisits();
			if (props?.clearAllState) props.clearAllState();
		};
	}, [props]);

	const getVisit = async (id: string): Promise<PromotionVisit> => {
		setIsLoading(true);
		setError(null);

		try {
			const response = await api.get<ResponseAPI>(`promotorvisita/online/id/${id}`);

			if (response.data && response.data.status === 200) {
				const visitData = JSON.parse(response.data.body);

				if (visitData.promotor && typeof visitData.promotor === "object") {
					visitData.promotorNome = visitData.promotor.razaoSocialNome;
				}

				return visitData;
			} else {
				const errorMsg = response.data?.message || "Visita não encontrada";
				throw new Error(errorMsg);
			}
		} catch (err) {
			const errorMessage =
				err instanceof Error ? err.message : "Erro ao buscar visita";
			setError(errorMessage);
			toast.error(errorMessage);
			throw err;
		} finally {
			setIsLoading(false);
		}
	};

	const handlegoBack = async () => {
		setError("");

		handleSelectPromotionToStartActions(undefined);

		if (props?.clearFilters) props.clearFilters();
		if (props?.clearSelectedVisits) props.clearSelectedVisits();
		if (props?.clearAllState) props.clearAllState();

		navigate("/visitas");
	};

	const saveVisit = async (visit: PromotionVisit): Promise<PromotionVisit> => {
		setIsLoading(true);
		setError(null);
		try {
			const response = await api.post<ResponseAPI>("promotorvisita/online", visit);

			if (response.data.status === 200) {
				return visit;
			} else {
				const errorMsg = response.data?.message || "Erro ao salvar visita";
				throw new Error(errorMsg);
			}
		} catch (err) {
			const errorMessage =
				err instanceof Error ? err.message : "Erro ao salvar visita";
			setError(errorMessage);
			toast.error(errorMessage);
			throw err;
		} finally {
			setIsLoading(false);
		}
	};

	const deleteVisit = async (id: string): Promise<void> => {
		setIsLoading(true);
		setError(null);

		try {
			const response = await api.delete<ResponseAPI>(`promotorvisita/online/id/${id}`);

			if (response.data && response.data.status !== 200) {
				throw new Error(response.data.message || "Erro ao excluir visita");
			}
		} catch (err) {
			const errorMessage =
				err instanceof Error ? err.message : "Erro ao excluir visita";
			setError(errorMessage);
			toast.error(errorMessage);
			throw err;
		} finally {
			setIsLoading(false);
		}
	};

	const clearPageState = () => {
		setError(null);
		if (props?.clearFilters) props.clearFilters();
		if (props?.clearSelectedVisits) props.clearSelectedVisits();
		if (props?.clearAllState) props.clearAllState();
	};

	return {
		isLoading,
		error,
		getVisit,
		saveVisit,
		deleteVisit,
		handlegoBack,
		clearPageState,
	};
}